import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar as CalendarIcon, Radio, Play, Film, Filter, ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AuthModal from '../components/AuthModal';
import SearchModal from '../components/SearchModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Category display names
const CATEGORY_LABELS = {
  'ÒPERA': 'Ópera',
  'BALLET': 'Ballet',
  'CONCERT': 'Concierto',
  'DOCUMENTAL': 'Documental',
  'PELÍCULA': 'Película'
};

const getCategoryLabel = (category) => CATEGORY_LABELS[category] || category;

const CalendarPage = () => {
  const [authOpen, setAuthOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedType, setSelectedType] = useState('all'); // all, directo, encore, grabado
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  
  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/content`);
      if (res.ok) {
        const data = await res.json();
        setContents(data);
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories
  const categories = [...new Set(contents.map(c => c.category).filter(Boolean))];

  // Helper function to check if a content's date has passed
  const isContentPast = (content) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (content.is_live && content.live_date) {
      const liveDate = new Date(content.live_date);
      liveDate.setHours(0, 0, 0, 0);
      return liveDate < today;
    }
    
    if (content.is_encore && content.encore_end_date) {
      const encoreEnd = new Date(content.encore_end_date);
      encoreEnd.setHours(0, 0, 0, 0);
      return encoreEnd < today;
    }
    
    return false;
  };

  // Filter contents that have dates
  const contentWithDates = contents.filter(content => {
    // Exclude past directos and encores
    if (isContentPast(content)) return false;

    // Must have some date
    const hasDate = content.is_live && content.live_date || 
                    content.is_encore && content.encore_start_date ||
                    content.premiere_date;
    if (!hasDate) return false;
    
    // Type filter
    if (selectedType !== 'all') {
      if (selectedType === 'directo' && !content.is_live) return false;
      if (selectedType === 'encore' && !content.is_encore) return false;
      if (selectedType === 'grabado' && (content.is_live || content.is_encore)) return false;
    }
    
    // Category filter
    if (selectedCategory !== 'all' && content.category !== selectedCategory) {
      return false;
    }
    
    return true;
  });

  // Get content date
  const getContentDate = (content) => {
    if (content.is_live && content.live_date) return new Date(content.live_date);
    if (content.is_encore && content.encore_start_date) return new Date(content.encore_start_date);
    if (content.premiere_date) return new Date(content.premiere_date);
    return null;
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1; // Monday = 0
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  // Get events for a specific date
  const getEventsForDate = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return contentWithDates.filter(content => {
      const contentDate = getContentDate(content);
      if (!contentDate) return false;
      const contentDateStr = contentDate.toISOString().split('T')[0];
      return contentDateStr === dateStr;
    });
  };

  // Get upcoming events (next 30 days)
  const getUpcomingEvents = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const futureDate = new Date(today);
    futureDate.setDate(futureDate.getDate() + 90);
    
    return contentWithDates
      .filter(content => {
        const contentDate = getContentDate(content);
        if (!contentDate) return false;
        return contentDate >= today && contentDate <= futureDate;
      })
      .sort((a, b) => getContentDate(a) - getContentDate(b));
  };

  const upcomingEvents = getUpcomingEvents();

  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const dayNames = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDayOfMonth = getFirstDayOfMonth(currentDate);

  const clearFilters = () => {
    setSelectedType('all');
    setSelectedCategory('all');
  };

  const hasActiveFilters = selectedType !== 'all' || selectedCategory !== 'all';

  // Events for selected date
  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="calendar-page">
      <Header 
        onOpenAuth={() => setAuthOpen(true)} 
        onOpenSearch={() => setSearchOpen(true)}
      />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <CalendarIcon className="w-8 h-8 text-[#e4002b]" />
            <h1 className="text-4xl md:text-5xl font-bold text-white">
              Calendario de Estrenos
            </h1>
          </div>
          <p className="text-white/60 text-lg max-w-2xl">
            Descubre los próximos directos, encores y grabados disponibles para tu cine.
          </p>
        </div>
      </div>

      {/* Filters Section */}
      <div className="sticky top-16 z-30 bg-[#0a0a0a]/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-wrap gap-3 items-center">
            {/* Type Filter */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowTypeDropdown(!showTypeDropdown);
                  setShowCategoryDropdown(false);
                }}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all ${
                  selectedType !== 'all' 
                    ? 'bg-[#e4002b]/20 border-[#e4002b] text-white' 
                    : 'bg-white/5 border-white/10 text-white/70 hover:border-white/30'
                }`}
                data-testid="type-filter"
              >
                <Radio className="w-4 h-4" />
                <span className="text-sm">
                  {selectedType === 'all' ? 'Tipo' : 
                   selectedType === 'directo' ? 'Directo' :
                   selectedType === 'encore' ? 'Encore' : 'Grabado'}
                </span>
                <ChevronDown className="w-4 h-4" />
              </button>
              
              {showTypeDropdown && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-[#1a1a1a] border border-white/10 rounded-lg shadow-xl z-50 py-1">
                  <button
                    onClick={() => { setSelectedType('all'); setShowTypeDropdown(false); }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-white/5 ${selectedType === 'all' ? 'text-[#e4002b]' : 'text-white/70'}`}
                  >
                    Todos los tipos
                  </button>
                  <button
                    onClick={() => { setSelectedType('directo'); setShowTypeDropdown(false); }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-white/5 flex items-center gap-2 ${selectedType === 'directo' ? 'text-[#e4002b]' : 'text-white/70'}`}
                  >
                    <span className="w-2 h-2 rounded-full bg-[#e4002b]" /> Directo
                  </button>
                  <button
                    onClick={() => { setSelectedType('encore'); setShowTypeDropdown(false); }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-white/5 flex items-center gap-2 ${selectedType === 'encore' ? 'text-[#e4002b]' : 'text-white/70'}`}
                  >
                    <span className="w-2 h-2 rounded-full bg-amber-500" /> Encore
                  </button>
                  <button
                    onClick={() => { setSelectedType('grabado'); setShowTypeDropdown(false); }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-white/5 flex items-center gap-2 ${selectedType === 'grabado' ? 'text-[#e4002b]' : 'text-white/70'}`}
                  >
                    <span className="w-2 h-2 rounded-full bg-white/50" /> Grabado
                  </button>
                </div>
              )}
            </div>

            {/* Category Filter */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowCategoryDropdown(!showCategoryDropdown);
                  setShowTypeDropdown(false);
                }}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all ${
                  selectedCategory !== 'all' 
                    ? 'bg-[#e4002b]/20 border-[#e4002b] text-white' 
                    : 'bg-white/5 border-white/10 text-white/70 hover:border-white/30'
                }`}
                data-testid="category-filter"
              >
                <Film className="w-4 h-4" />
                <span className="text-sm">
                  {selectedCategory === 'all' ? 'Género' : getCategoryLabel(selectedCategory)}
                </span>
                <ChevronDown className="w-4 h-4" />
              </button>
              
              {showCategoryDropdown && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-[#1a1a1a] border border-white/10 rounded-lg shadow-xl z-50 py-1">
                  <button
                    onClick={() => { setSelectedCategory('all'); setShowCategoryDropdown(false); }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-white/5 ${selectedCategory === 'all' ? 'text-[#e4002b]' : 'text-white/70'}`}
                  >
                    Todos los géneros
                  </button>
                  {categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => { setSelectedCategory(cat); setShowCategoryDropdown(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-white/5 ${selectedCategory === cat ? 'text-[#e4002b]' : 'text-white/70'}`}
                    >
                      {getCategoryLabel(cat)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2.5 text-white/60 hover:text-white text-sm"
              >
                <X className="w-4 h-4" />
                Limpiar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#e4002b] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Calendar */}
            <div className="lg:col-span-2">
              <div className="bg-[#141414] rounded-2xl p-6 border border-white/10">
                {/* Calendar Header */}
                <div className="flex items-center justify-between mb-6">
                  <button
                    onClick={prevMonth}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5 text-white" />
                  </button>
                  <h2 className="text-xl font-bold text-white">
                    {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h2>
                  <button
                    onClick={nextMonth}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <ChevronRight className="w-5 h-5 text-white" />
                  </button>
                </div>

                {/* Day Names */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {dayNames.map(day => (
                    <div key={day} className="text-center text-white/50 text-sm py-2">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-1">
                  {/* Empty cells for days before first day of month */}
                  {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                    <div key={`empty-${i}`} className="aspect-square" />
                  ))}
                  
                  {/* Days of month */}
                  {Array.from({ length: daysInMonth }).map((_, i) => {
                    const day = i + 1;
                    const events = getEventsForDate(day);
                    const hasEvents = events.length > 0;
                    const isToday = new Date().toDateString() === new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();
                    const isSelected = selectedDate === day;
                    
                    return (
                      <button
                        key={day}
                        onClick={() => setSelectedDate(isSelected ? null : day)}
                        className={`aspect-square rounded-lg flex flex-col items-center justify-center relative transition-all ${
                          isSelected 
                            ? 'bg-[#e4002b] text-white' 
                            : isToday 
                              ? 'bg-white/10 text-white ring-2 ring-[#e4002b]' 
                              : hasEvents 
                                ? 'bg-white/5 text-white hover:bg-white/10' 
                                : 'text-white/50 hover:bg-white/5'
                        }`}
                      >
                        <span className="text-sm font-medium">{day}</span>
                        {hasEvents && (
                          <div className="flex gap-0.5 mt-1">
                            {events.slice(0, 3).map((event, idx) => (
                              <span 
                                key={idx} 
                                className={`w-1.5 h-1.5 rounded-full ${
                                  event.is_live ? 'bg-[#e4002b]' : 
                                  event.is_encore ? 'bg-amber-500' : 'bg-white/50'
                                }`}
                              />
                            ))}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="flex items-center gap-6 mt-6 pt-4 border-t border-white/10">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-[#e4002b]" />
                    <span className="text-white/60 text-sm">Directo</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-amber-500" />
                    <span className="text-white/60 text-sm">Encore</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-white/50" />
                    <span className="text-white/60 text-sm">Grabado</span>
                  </div>
                </div>
              </div>

              {/* Selected Date Events */}
              {selectedDate && (
                <div className="mt-6 bg-[#141414] rounded-2xl p-6 border border-white/10">
                  <h3 className="text-lg font-semibold text-white mb-4">
                    Eventos del {selectedDate} de {monthNames[currentDate.getMonth()]}
                  </h3>
                  {selectedDateEvents.length === 0 ? (
                    <p className="text-white/50">No hay eventos para esta fecha</p>
                  ) : (
                    <div className="space-y-3">
                      {selectedDateEvents.map(content => (
                        <Link
                          key={content.id}
                          to={`/content/${content.id}`}
                          onClick={() => window.scrollTo(0, 0)}
                          className="flex items-center gap-4 p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-colors group"
                        >
                          <div className="w-16 h-20 rounded-lg overflow-hidden flex-shrink-0">
                            <img
                              src={content.image || content.hero_image}
                              alt={content.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {content.is_live && (
                                <span className="flex items-center gap-1 px-2 py-0.5 bg-[#e4002b] text-white text-xs rounded font-medium">
                                  <Radio className="w-3 h-3" /> DIRECTO
                                </span>
                              )}
                              {content.is_encore && (
                                <span className="px-2 py-0.5 bg-amber-500 text-black text-xs rounded font-medium">
                                  ENCORE
                                </span>
                              )}
                              <span className="text-white/40 text-xs">
                                {getCategoryLabel(content.category)}
                              </span>
                            </div>
                            <h4 className="text-white font-medium group-hover:text-[#e4002b] transition-colors truncate">
                              {content.title}
                            </h4>
                            <p className="text-[#e4002b] text-sm">{content.composer}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Upcoming Events Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-[#141414] rounded-2xl p-6 border border-white/10 sticky top-32">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-[#e4002b]" />
                  Próximos Estrenos
                </h3>
                
                {upcomingEvents.length === 0 ? (
                  <p className="text-white/50 text-sm">No hay eventos próximos</p>
                ) : (
                  <div className="space-y-4 max-h-[600px] overflow-y-auto">
                    {upcomingEvents.slice(0, 10).map(content => {
                      const contentDate = getContentDate(content);
                      return (
                        <Link
                          key={content.id}
                          to={`/content/${content.id}`}
                          onClick={() => window.scrollTo(0, 0)}
                          className="block group"
                        >
                          <div className="flex items-start gap-3">
                            {/* Date badge */}
                            <div className="flex-shrink-0 w-12 text-center">
                              <div className="text-[#e4002b] text-xs font-medium uppercase">
                                {monthNames[contentDate.getMonth()].slice(0, 3)}
                              </div>
                              <div className="text-white text-xl font-bold">
                                {contentDate.getDate()}
                              </div>
                            </div>
                            
                            {/* Content info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1 mb-1">
                                {content.is_live && (
                                  <span className="w-2 h-2 rounded-full bg-[#e4002b]" />
                                )}
                                {content.is_encore && (
                                  <span className="w-2 h-2 rounded-full bg-amber-500" />
                                )}
                                {!content.is_live && !content.is_encore && (
                                  <span className="w-2 h-2 rounded-full bg-white/50" />
                                )}
                                <span className="text-white/40 text-xs">
                                  {getCategoryLabel(content.category)}
                                </span>
                              </div>
                              <h4 className="text-white text-sm font-medium group-hover:text-[#e4002b] transition-colors line-clamp-2">
                                {content.title}
                              </h4>
                              <p className="text-white/50 text-xs mt-0.5">{content.composer}</p>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
      
      <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
};

export default CalendarPage;
